import React, { useState, useEffect } from 'react';
import { TEAM_ICONS, getAllTeamIcons, saveTeamIcon } from './utils/teamIcons';
import teamManagementApi from './services/teamManagementApi';

const TeamIconSelector = ({ teamName, onIconChange, currentIconKey = null, currentUser = null, leagueId = null, disabled = false }) => {
  const [selectedIcon, setSelectedIcon] = useState(currentIconKey || 'football');
  const [showSelector, setShowSelector] = useState(false);

  // Load the saved icon from the API when team changes; reset immediately so prior team icon does not linger
  useEffect(() => {
    setShowSelector(false);

    if (currentIconKey) {
      setSelectedIcon(currentIconKey);
      return;
    }

    const defaultKey = 'football';
    setSelectedIcon(defaultKey);

    if (!teamName || !leagueId) {
      return;
    }

    let cancelled = false;
    const loadIcon = async () => {
      try {
        const iconKey = await teamManagementApi.getTeamIcon(leagueId, teamName, currentUser);
        if (cancelled) return;
        setSelectedIcon(iconKey && TEAM_ICONS[iconKey] ? iconKey : defaultKey);
      } catch (error) {
        console.error('Error loading team icon:', error);
        if (!cancelled) setSelectedIcon(defaultKey);
      }
    };
    loadIcon();

    return () => {
      cancelled = true;
    };
  }, [teamName, leagueId, currentUser, currentIconKey]);

  const handleIconSelect = (iconKey) => {
    setSelectedIcon(iconKey);
    saveTeamIcon(teamName, iconKey, leagueId, currentUser);
    if (onIconChange) {
      onIconChange(iconKey);
    }
    setShowSelector(false);
  };

  const currentIcon = TEAM_ICONS[selectedIcon] || TEAM_ICONS.football;

  return (
    <div style={{ position: 'relative' }}>
      {/* Current Icon Display */}
      <button
        onClick={() => !disabled && setShowSelector(!showSelector)}
        disabled={disabled}
        className={`ffb-btn ${disabled ? "ffb-btn-disabled" : ""}`}
        style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px', 
          fontSize: '1.1em', 
          fontWeight: 600, 
          color: disabled ? '#64748b' : '#1e3a8a', 
          transition: 'all 0.2s ease', 
          boxShadow: '0 2px 4px rgba(30, 58, 92, 0.1)',
          opacity: disabled ? 0.6 : 1
        }}
        onMouseOver={(e) => {
          if (!disabled) {
            e.currentTarget.style.borderColor = '#2563eb';
            e.currentTarget.style.boxShadow = '0 4px 8px rgba(30, 58, 92, 0.15)';
          }
        }}
        onMouseOut={(e) => {
          if (!disabled) {
            e.currentTarget.style.borderColor = '#e0e7ff';
            e.currentTarget.style.boxShadow = '0 2px 4px rgba(30, 58, 92, 0.1)';
          }
        }}
      >
        <span style={{ fontSize: '1.3em' }}>{currentIcon.emoji}</span>
        <span style={{ fontSize: '0.9em', marginLeft: 'auto' }}>
          {showSelector ? '▲' : '▼'}
        </span>
      </button>

      {/* Icon Selector Dropdown */}
      {showSelector && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            background: 'white',
            border: '2px solid #e0e7ff',
            borderRadius: '8px',
            boxShadow: '0 8px 24px rgba(30, 58, 92, 0.15)',
            zIndex: 1000,
            maxHeight: '300px',
            overflowY: 'auto',
            marginTop: '4px',
            padding: '12px'
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(50px, 1fr))',
              gap: '8px',
              maxWidth: '300px'
            }}
          >
            {getAllTeamIcons().map(icon => (
              <button
                key={icon.key}
                onClick={() => handleIconSelect(icon.key)}
                className="ffb-btn"
                style={{ background: selectedIcon === icon.key ? '#dbeafe' : 'white', border: selectedIcon === icon.key ? '2px solid #2563eb' : '1px solid #e0e7ff', borderRadius: '8px', padding: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5em', transition: 'all 0.2s ease', minHeight: '50px', minWidth: '50px' }}
                onMouseOver={(e) => {
                  if (selectedIcon !== icon.key) {
                    e.currentTarget.style.background = '#f0f9ff';
                    e.currentTarget.style.borderColor = '#93c5fd';
                    e.currentTarget.style.transform = 'scale(1.05)';
                  }
                }}
                onMouseOut={(e) => {
                  if (selectedIcon !== icon.key) {
                    e.currentTarget.style.background = 'white';
                    e.currentTarget.style.borderColor = '#e0e7ff';
                    e.currentTarget.style.transform = 'scale(1)';
                  }
                }}
                title={icon.name}
              >
                {icon.emoji}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Click outside to close */}
      {showSelector && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 999
          }}
          onClick={() => setShowSelector(false)}
        />
      )}
    </div>
  );
};

export default TeamIconSelector; 